import React, { useEffect, useState, useRef, useMemo } from "react";
import { Drawer } from "antd";
import type { DrawerProps } from "antd";
import { sizes } from "../../theme/tokens/sizes";
import { ChevronUpIcon, ChevronDownIcon } from "../icons";

/**
 * SpecDrawer
 * - Wrapper around Ant Design Drawer so you can set sane global defaults in one place.
 * - Adds a semantic `size` prop that maps to HEIGHT for bottom drawers.
 * - Supports click-outside close even when `mask` is disabled (overlay=false).
 * - Supports swipe gestures to resize between minimized/default/large states.
 *
 * Notes:
 * - Ant Design Drawer does NOT have a built-in `size` prop, so this is *our* prop.
 * - For placement="bottom" (your use case), `height` is the key dimension.
 * - `height` prop always wins over `size` (escape hatch).
 */

type SpecDrawerSize = "minimized" | "default" | "large";

export interface SpecDrawerProps extends Omit<DrawerProps, 'size'> {
  /**
   * Whether to show the background overlay (mask).
   * Defaults to true.
   * Maps to Ant Design's `mask` prop.
   */
  overlay?: boolean;

  /**
   * Whether to close the drawer when clicking outside.
   * Defaults to true.
   * - If overlay=true, maps to Ant's `maskClosable`.
   * - If overlay=false, we add a document mousedown listener and call onClose when you click outside.
   */
  closeOnClickOutside?: boolean;

  /**
   * Semantic sizing for the drawer.
   * For bottom drawers: maps to HEIGHT using your design tokens.
   *
   * IMPORTANT: Ant Drawer itself does not understand `size`.
   * This wrapper reads it and sets `height`.
   */
  size?: SpecDrawerSize;

  /**
   * Callback fired when the size changes (typically via swipe gesture).
   * Use this to control the drawer size externally.
   */
  onSizeChange?: (size: SpecDrawerSize) => void;

  /**
   * Enable/disable swipe-to-resize gestures.
   * When true, users can drag the drawer up/down to snap between sizes.
   * Defaults to false.
   */
  swipeable?: boolean;

  /**
   * Show/hide the drag handle bar at the top.
   * Defaults to true when swipeable is true.
   */
  showDragHandle?: boolean;
}

export default function SpecDrawer({
  overlay = true,
  closeOnClickOutside = true,
  size = "default",
  onSizeChange,
  swipeable = false,
  showDragHandle,

  // Allow callers to override placement, but default to your app pattern
  placement = "bottom",

  // Allow callers to hard-set height/width (escape hatches)
  height,
  width,

  // Everything else (open, onClose, title, etc.)
  ...props
}: SpecDrawerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const drawerContentRef = useRef<HTMLElement | null>(null);

  // Helper to convert size value (number or percentage string) to pixels
  // Caps heights at viewport to prevent overflow in mobile landscape mode
  const sizeToPixels = (sizeValue: number | string): number => {
    const viewportHeight = window.innerHeight;
    
    if (typeof sizeValue === 'number') {
      // Cap fixed pixel values at 90% of viewport to prevent overflow
      return Math.min(sizeValue, viewportHeight * 0.9);
    }
    // Handle percentage strings like "90%"
    if (typeof sizeValue === 'string' && sizeValue.endsWith('%')) {
      const percentage = parseFloat(sizeValue);
      return (viewportHeight * percentage) / 100;
    }
    // Fallback: try to parse as number
    return parseFloat(sizeValue) || 0;
  };

  // Size to height mapping (memoized to prevent re-creation on every render)
  const sizeToHeight: Record<SpecDrawerSize, number> = useMemo(() => ({
    minimized: sizeToPixels(sizes.drawer.minimized),
    default: sizeToPixels(sizes.drawer.default),
    large: sizeToPixels(sizes.drawer.large),
  }), []);

  /**
   * Resolve semantic size -> pixel height/width using your token map.
   * For your current usage (placement="bottom"), we primarily use HEIGHT.
   *
   * `height` prop always wins if provided.
   * For calculations, we use pixel values. For the Drawer prop, we preserve percentage strings.
   */
  const resolvedHeightForProp = height 
    ? height
    : (typeof sizes.drawer[size] === 'string' ? sizes.drawer[size] : sizeToHeight[size]);
  
  const resolvedHeight = height 
    ? (typeof height === 'string' && height.endsWith('%') 
        ? sizeToPixels(height) 
        : (typeof height === 'string' ? parseInt(height, 10) : height))
    : sizeToHeight[size];

  /**
   * Optional: if you ever use left/right drawers later,
   * you can also drive width from the same size tokens.
   * For bottom drawers, width is typically full-viewport and not needed.
   */
  const resolvedWidth = width 
    ? (typeof width === 'string' && width.endsWith('%')
        ? sizeToPixels(width)
        : (typeof width === 'string' ? parseInt(width, 10) : width))
    : sizeToHeight[size];

  /**
   * Handle click-outside when overlay (mask) is disabled.
   * When overlay=true, Ant already handles outside-click via maskClosable.
   */
  useEffect(() => {
    // Only apply custom outside-click behavior when:
    // - overlay is off (no mask)
    // - drawer is open
    // - user wants click-outside closing enabled
    if (overlay || !props.open || !closeOnClickOutside) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // If click is not inside the drawer panel content, treat it as outside click
      // `.ant-drawer-content` is inside the panel (safe to click)
      if (!target.closest(".ant-drawer-content")) {
        props.onClose?.(e as any);
      }
    };

    // Use mousedown to match typical "dismiss on outside click" behavior
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [overlay, closeOnClickOutside, props.open, props.onClose]);

  /**
   * Handle swipe-to-resize gestures when swipeable is enabled.
   */
  useEffect(() => {
    if (!swipeable || !props.open) return;

    // Wait for drawer to be mounted in DOM
    const checkDrawer = () => {
      const drawerElement = document.querySelector('.ant-drawer-content-wrapper') as HTMLElement;
      if (drawerElement) {
        drawerContentRef.current = drawerElement;
      }
    };
    
    // Check immediately and after a short delay to ensure DOM is ready
    checkDrawer();
    const timer = setTimeout(checkDrawer, 100);

    const handleStart = (clientY: number, target: HTMLElement) => {
      // Only start drag if user grabs the drag handle or header
      if (!target.closest('.drawer-drag-handle') && !target.closest('.ant-drawer-header')) {
        return;
      }

      // Read the ACTUAL current height from the DOM element, not the React prop
      // This fixes the bug where stale height values cause incorrect drag calculations
      const currentHeight = drawerContentRef.current?.offsetHeight || resolvedHeight;

      setIsDragging(true);
      dragStartYRef.current = clientY;
      dragStartHeightRef.current = currentHeight;
    };

    const handleMove = (clientY: number) => {
      if (!isDragging || !drawerContentRef.current) return;
      
      const deltaY = dragStartYRef.current - clientY; // positive = drag up, negative = drag down
      const newHeight = dragStartHeightRef.current + deltaY;
      const viewportHeight = window.innerHeight;
      
      // Constrain between min and max, and cap at 90% of viewport to prevent overflow
      const constrainedHeight = Math.max(
        sizeToHeight.minimized,
        Math.min(sizeToHeight.large, Math.min(newHeight, viewportHeight * 0.9))
      );
      
      // Apply height dynamically during drag (disable transition for smooth dragging)
      drawerContentRef.current.style.transition = 'none';
      drawerContentRef.current.style.height = `${constrainedHeight}px`;
    };

    const handleEnd = (clientY: number) => {
      if (!isDragging || !drawerContentRef.current) return;
      
      setIsDragging(false);
      const deltaY = dragStartYRef.current - clientY;
      const finalHeight = dragStartHeightRef.current + deltaY;
      
      // Snap to nearest size
      let newSize: SpecDrawerSize;
      const midLow = (sizeToHeight.minimized + sizeToHeight.default) / 2;
      const midHigh = (sizeToHeight.default + sizeToHeight.large) / 2;
      
      if (finalHeight < midLow) {
        newSize = "minimized";
      } else if (finalHeight < midHigh) {
        newSize = "default";
      } else {
        newSize = "large";
      }
      
      const targetHeight = sizeToHeight[newSize];
      
      // Set height to target immediately to prevent intermediate 193px height
      // Keep inline style set - don't clear it. This ensures the correct height is maintained
      // even if React's prop update is delayed. The inline style will be updated on next drag.
      drawerContentRef.current.style.height = `${targetHeight}px`;
      
      // Re-enable transition for smooth snap
      drawerContentRef.current.style.transition = '';
      
      // Notify parent of size change
      if (newSize !== size && onSizeChange) {
        onSizeChange(newSize);
      }
      
      // NOTE: We intentionally do NOT clear the inline style here.
      // Keeping it ensures the correct height is maintained. On the next drag,
      // handleMove will update it to the new dragged height, and handleEnd will
      // set it to the new target height. This prevents the 193px intermediate state.
    };

    // Touch events
    const onTouchStart = (e: TouchEvent) => {
      handleStart(e.touches[0].clientY, e.target as HTMLElement);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      handleEnd(e.changedTouches[0].clientY);
    };

    // Mouse events (for desktop testing)
    const onMouseDown = (e: MouseEvent) => {
      handleStart(e.clientY, e.target as HTMLElement);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.clientY);
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      handleEnd(e.clientY);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [swipeable, isDragging, props.open, size, onSizeChange, resolvedHeight, sizeToHeight]);

  /**
   * Apply resolvedHeight/resolvedWidth based on placement.
   * - top/bottom drawers use `height`
   * - left/right drawers use `width`
   *
   * This avoids passing irrelevant dimensions.
   */
  const dimensionProps: Partial<DrawerProps> =
    placement === "bottom" || placement === "top"
      ? { height: resolvedHeightForProp }
      : { width: resolvedWidth };

  // Determine which chevron buttons should be enabled
  const canGoUp = size !== "large";
  const canGoDown = size !== "minimized";

  // Handle chevron button clicks
  const handleChevronUp = () => {
    if (!canGoUp || !onSizeChange) return;
    const nextSize = size === "minimized" ? "default" : "large";
    onSizeChange(nextSize);
  };

  const handleChevronDown = () => {
    if (!canGoDown || !onSizeChange) return;
    const nextSize = size === "large" ? "default" : "minimized";
    onSizeChange(nextSize);
  };

  // Determine if drag handle should be shown (defaults to true when swipeable)
  const shouldShowDragHandle = showDragHandle ?? swipeable;

  // Wrap title with drag handle when swipeable
  const titleWithDragHandle = swipeable ? (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      {shouldShowDragHandle && (
        <div
          className="drawer-drag-handle"
          style={{
            width: '40px',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '2px',
            margin: '0 auto 24px', /* 4px + 16px = 20px */
            cursor: 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        />
      )}
      {props.title}
      {/* Chevron buttons - absolutely positioned to keep title centered */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: shouldShowDragHandle ? '8px' : '-8px',
          display: 'flex',
          flexDirection: 'row',
          gap: '0px',
        }}
      >

        <button
          onClick={handleChevronUp}
          disabled={!canGoUp}
          aria-label="Expand drawer"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '4px',
            cursor: canGoUp ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.2s ease',
          }}
        >
          <ChevronUpIcon size="s" tone={canGoUp ? "lightGrey" : "darkGrey"} />
        </button>

        <button
          onClick={handleChevronDown}
          disabled={!canGoDown}
          aria-label="Collapse drawer"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '4px',
            cursor: canGoDown ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.2s ease',
          }}
        >
          <ChevronDownIcon size="s" tone={canGoDown ? "lightGrey" : "darkGrey"} />
        </button>
      </div>
    </div>
  ) : props.title;

  // Prevent scrolling when drawer is minimized
  useEffect(() => {
    if (size === 'minimized' && props.open) {
      const wrapper = document.querySelector('.spec-drawer--minimized .ant-drawer-content-wrapper');
      const preventScroll = (e: Event) => e.preventDefault();
      wrapper?.addEventListener('wheel', preventScroll, { passive: false });
      wrapper?.addEventListener('touchmove', preventScroll, { passive: false });
      return () => {
        wrapper?.removeEventListener('wheel', preventScroll);
        wrapper?.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [size, props.open]);

  return (
    <Drawer
      // Default behavior
      placement={placement}
      mask={overlay}
      maskClosable={closeOnClickOutside}
      rootClassName={`spec-drawer spec-drawer--${size}`}
      {...dimensionProps}
      // Your styling defaults
      styles={{
        mask: {
          background: 'rgba(17, 17, 17, 0.6)', // colorBgSpotlight - enables specLight shadows
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
        body: {
          background: "#000",
          paddingBottom: '46px',
        },
        header: {
          background: "#000",
          cursor: swipeable ? "grab" : undefined, // Show grab cursor on header when swipeable
          paddingTop: shouldShowDragHandle ? 12 : undefined, // Add top padding for drag handle
          borderBottom: 'none',
        },
        footer: {
          background: "#000",
          padding: 0,
          borderTop: 'none',
        },
        // Smoothly animate height changes for bottom/top drawers (unless dragging)
        wrapper: {
          transition: isDragging ? "none" : "height 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
          boxShadow: '0px 20px 25px rgba(0,0,0,0.1), 0px 10px 10px rgba(0,0,0,0.04)', // specLight4
          borderTop: '1px solid #333', // Match bottom sheet border style
          borderLeft: '1px solid #333',
          borderRight: '1px solid #333',
        },
        // Allow callers to still pass styles.*; their values win if they set the same keys
        ...(props.styles ?? {}),
      }}
      // Let callers override anything else (open, onClose, title, etc.)
      {...props}
      // Override title with our wrapped version
      title={titleWithDragHandle}
    >
      {props.children}
    </Drawer>
  );
}